import React, { useCallback, useRef, useState } from "react";
import "../wizard.component.scoped.scss";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {
  CreateWorkspaceWizardData,
  WizardStepProps,
} from "../../../types/create-workspace-wizard";
import {
  capitalChar,
  digitChar,
  specialChar,
} from "../../../../../statics/validators";
import { FileSizes } from "../../../../../statics/file-sizes";
import { imageTypes } from "../../../../../statics/file-types";
import { turncate } from "../../../../../helpers";
import { alertToastConfig } from "../../../../../statics/alert-config";

type Props = WizardStepProps<CreateWorkspaceWizardData["step2"]>;

const WizardStep2: React.FC<Props> = ({ data, onChange, errors }) => {
  const { t } = useTranslation();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const [touched, setTouched] = React.useState<
    Partial<Record<keyof CreateWorkspaceWizardData["step2"], boolean>>
  >({});

  const updateField = (
    field: keyof CreateWorkspaceWizardData["step2"],
    value: string,
  ) => {
    setTouched(
      (
        prev: Partial<
          Record<keyof CreateWorkspaceWizardData["step2"], boolean>
        >,
      ) => ({ ...prev, [field]: true }),
    );
    onChange({ ...data, [field]: value });
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const onKeyDown = (e: any) => {
    if (e.getModifierState("CapsLock")) {
      setCapsLock(true);
    } else {
      setCapsLock(false);
    }
  };

  function getImageFileInfo(
    type: string,
  ): { fileType: string; maxSize: number } | null {
    switch (type) {
      case "image/jpeg":
        return { fileType: "jpg", maxSize: FileSizes.jpg.maxSize };
      case "image/png":
        return { fileType: "png", maxSize: FileSizes.png.maxSize };
      default:
        return null;
    }
  }

  const getImagePreview = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const showAlertMessages = useCallback(async (messages: string[]) => {
    const MySwal = withReactContent(Swal);
    for (const message of messages) {
      await MySwal.fire({
        icon: "error",
        title: message,
        ...alertToastConfig,
      });
    }
  }, []);
  const validateAndApplyLogo = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const file = files[0];
      const info = getImageFileInfo(file.type);
      if (!info) {
        await showAlertMessages([
          t("WORKSPACE_WIZARD.ONLY_JPG_JPEG_PNG_IMAGES_SUPPORTED"),
        ]);
        return;
      }
      const fileName = file.name.replace(/\.[^.]+$/, "");
      if (file.size > info.maxSize) {
        await showAlertMessages([
          `${turncate(
            fileName,
            20,
          )} exceeds max ${info.fileType.toUpperCase()} size (${
            info.maxSize / 1024 / 1000
          } MB)`,
        ]);
        return;
      }
      try {
        const src = await getImagePreview(file);
        onChange({ ...data, logoPreviewUrl: src, logoFile: file });
      } catch {
        await showAlertMessages([
          t("WORKSPACE_WIZARD.COULD_NOT_READ_THE_IMAGE_FILE"),
        ]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, onChange, showAlertMessages],
  );

  const clickUpload = useCallback(() => {
    setTouched((prev) => ({ ...prev, logoPreviewUrl: true }));
    inputFileRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      await validateAndApplyLogo(files);
      if (inputFileRef.current) inputFileRef.current.value = "";
    },
    [validateAndApplyLogo],
  );

  return (
    <>
      <Box
        className="global__box__content product__details__content__form p-0 mb-5"
        style={{ height: "fit-content" }}
      >
        <Box className="global__box__content__header">
          <Typography
            className="text-capitalize"
            variant="h3"
            color="textPrimary"
          >
            {t("WORKSPACE_WIZARD.WORKSPACE_INFORMATION")}
          </Typography>
        </Box>
        <Box className="global__box__content__body">
          <Box className="form__item row-col-3">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.LOGO")}
            </Typography>
            <Typography variant="subtitle2">
              Logo 150x150px JPEG, PNG Image
            </Typography>
            <Box className="logo-update">
              <img
                src={
                  data.logoPreviewUrl
                    ? data.logoPreviewUrl
                    : "/icons/onboarding/cameraIcon.svg"
                }
                alt="logo"
                className={data.logoPreviewUrl ? "image-full" : "camera-icon"}
                onClick={clickUpload}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    clickUpload();
                  }
                }}
              />
              <input
                type="file"
                ref={inputFileRef}
                onChange={onFileChange}
                style={{ display: "none" }}
                accept={imageTypes}
              />
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.WORKSPACE_NAME")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.WORKSPACE_NAME")}
                variant="standard"
                name="workspace"
                value={data.workspace}
                className="w-100"
                onChange={(e) => updateField("workspace", e.target.value)}
                inputProps={{
                  maxLength: 30,
                }}
              />
              {touched.workspace && errors?.workspace && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.workspace === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t(
                        "INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED",
                      )}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.CONFIGURATION_NAME")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.CONFIGURATION_NAME")}
                variant="standard"
                name="configurationName"
                value={data.configurationName}
                className="w-100"
                onChange={(e) =>
                  updateField("configurationName", e.target.value)
                }
                inputProps={{
                  maxLength: 30,
                }}
              />
              <Typography
                variant="body2"
                className="my-2"
                color="textSecondary"
                style={{ fontWeight: 300, width: "80%" }}
              >
                {t("WORKSPACE_WIZARD.WORKSPACE_CONFIGURATION_HINT_TEXT")}
              </Typography>
              {touched.configurationName && errors?.configurationName && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.configurationName === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t("WORKSPACE_WIZARD.INVALID_CONFIGURATION_NAME_FORMAT")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        className="global__box__content product__details__content__form p-0"
        style={{ height: "fit-content" }}
      >
        <Box className="global__box__content__header">
          <Typography
            className="text-capitalize"
            variant="h3"
            color="textPrimary"
          >
            {t("WORKSPACE_WIZARD.STANDARD_PASSWORD")}
          </Typography>
        </Box>
        <Box className="global__box__content__body">
          <Box className="form__item">
            <Typography variant="subtitle2">{t("DEFAULT_PASSWORD")}</Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                type={passwordVisibility ? "text" : "password"}
                placeholder={t("DEFAULT_PASSWORD")}
                value={data.defaultPassword}
                autoComplete="off"
                name="defaultPassword"
                onChange={(e) => updateField("defaultPassword", e.target.value)}
                onKeyDown={onKeyDown}
                autoFocus={false}
                inputProps={{
                  minLength: 8,
                }}
                InputProps={{
                  autoComplete: "one-time-code",
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className="toggle-password"
                        aria-label="toggle password visibility"
                        onClick={setPasswordVisibility.bind(
                          null,
                          !passwordVisibility,
                        )}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {passwordVisibility ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body1" className="my-2">
                {t("WORKSPACE_WIZARD.STANDARD_PASSWORD_DESCRIPTION")}
              </Typography>
              {capsLock && (
                <div className="error-msg capsLock">
                  {t("caps lock is turned on")}
                </div>
              )}
              <div className="error-msg pt-1">
                <Typography
                  variant="body2"
                  style={{
                    color:
                      data.defaultPassword.length < 8 ||
                      data.defaultPassword.length > 15
                        ? ""
                        : "#a6ba8d",
                  }}
                >
                  {t("length 8 to 15 characters")}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: specialChar.test(data.defaultPassword)
                      ? "#a6ba8d"
                      : "",
                  }}
                >
                  {t("at least 1 special character")}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: capitalChar.test(data.defaultPassword)
                      ? "#a6ba8d"
                      : "",
                  }}
                >
                  {t("at least 1 capital letter")}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: digitChar.test(data.defaultPassword)
                      ? "#a6ba8d"
                      : "",
                  }}
                >
                  {t("at least 1 digit")}
                </Typography>
              </div>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default WizardStep2;
